import { useCallback, useEffect, useRef, useState } from "react";
import { Share2, X, Download, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SITE_URL = "https://digitalrevolutionradio.com";
const SITE_HOST = "DIGITALREVOLUTIONRADIO.COM";
const CANVAS_W = 1200;
const CANVAS_H = 630;

/**
 * Word-wrap a string across multiple Canvas lines. Returns the array of
 * lines that fit under maxWidth at the current ctx font. We ellipsize the
 * final line if the text overruns maxLines.
 */
function wrapLines(ctx, text, maxWidth, maxLines) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  for (const w of words) {
    const test = current ? `${current} ${w}` : w;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = w;
      if (lines.length >= maxLines) break;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  if (lines.length === maxLines) {
    // ellipsize last line if the source was longer than what fit
    const joined = lines.join(" ");
    if (joined.length < (text || "").length) {
      let last = lines[maxLines - 1];
      while (ctx.measureText(last + "…").width > maxWidth && last.length > 1) {
        last = last.slice(0, -1);
      }
      lines[maxLines - 1] = last + "…";
    }
  }
  return lines;
}

/**
 * Paint the DRR-branded "Now Spinning" card onto a canvas at 1200×630
 * (standard OG image size — social platforms will render this at the
 * correct aspect on any card preview).
 *
 * Brutalist palette: black base, concrete gray sublayer, red slash, sharp
 * corners, glitch scan lines. Track title dominates. Bottom strip anchors
 * the DRR brand + host.
 */
function paintCard(ctx, { title, artist }) {
  const W = CANVAS_W;
  const H = CANVAS_H;

  // 1. black base
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, W, H);

  // 2. concrete gray sublayer with faint diagonal stripes (brutalist texture)
  ctx.fillStyle = "#18181b";
  ctx.fillRect(60, 60, W - 120, H - 120);
  ctx.save();
  ctx.beginPath();
  ctx.rect(60, 60, W - 120, H - 120);
  ctx.clip();
  ctx.strokeStyle = "rgba(220,38,38,0.09)";
  ctx.lineWidth = 2;
  for (let x = -H; x < W; x += 22) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + H, H);
    ctx.stroke();
  }
  ctx.restore();

  // 3. left red slash column
  ctx.fillStyle = "#dc2626";
  ctx.fillRect(60, 60, 14, H - 120);

  // 4. faint horizontal scan lines
  ctx.save();
  ctx.beginPath();
  ctx.rect(74, 60, W - 60 - 74, H - 120);
  ctx.clip();
  ctx.fillStyle = "rgba(250,250,250,0.02)";
  for (let y = 60; y < H - 60; y += 4) {
    ctx.fillRect(74, y, W - 60 - 74, 2);
  }
  ctx.restore();

  // 5. top eyebrow: //_NOW_SPINNING · LIVE
  ctx.fillStyle = "#a1a1aa";
  ctx.font = "700 22px 'IBM Plex Mono', ui-monospace, monospace";
  ctx.textBaseline = "top";
  const eyebrow = "//_NOW_SPINNING · LIVE";
  const eyebrowY = 100;
  ctx.fillText(eyebrow, 110, eyebrowY);

  // red LIVE dot next to the eyebrow
  const eyebrowW = ctx.measureText(eyebrow).width;
  ctx.fillStyle = "#dc2626";
  ctx.beginPath();
  ctx.arc(110 + eyebrowW + 22, eyebrowY + 11, 8, 0, Math.PI * 2);
  ctx.fill();

  // 6. Track title — bold, huge, upper-case, wraps to 3 lines
  const cleanTitle = (title || "SILENCE BETWEEN SPINS").toUpperCase();
  ctx.fillStyle = "#fafafa";
  ctx.font = "800 92px 'Archivo Black', 'Impact', system-ui, sans-serif";
  const titleMaxW = W - 220;
  const titleLines = wrapLines(ctx, cleanTitle, titleMaxW, 3);
  let ty = 170;
  const lineH = 92;
  for (const line of titleLines) {
    ctx.fillText(line, 110, ty);
    ty += lineH;
  }

  // 7. Artist — big red accent under title
  const cleanArtist = (artist || "DIGITAL REVOLUTION RADIO").toUpperCase();
  ctx.fillStyle = "#dc2626";
  ctx.font = "700 40px 'IBM Plex Mono', ui-monospace, monospace";
  const artistY = Math.min(ty + 20, H - 200);
  const artistLines = wrapLines(ctx, cleanArtist, titleMaxW, 1);
  ctx.fillText(artistLines[0] || "", 110, artistY);

  // 8. Bottom black strip with DRR brand
  const stripY = H - 130;
  ctx.fillStyle = "#000000";
  ctx.fillRect(60, stripY, W - 120, 70);
  ctx.strokeStyle = "#3f3f46";
  ctx.lineWidth = 2;
  ctx.strokeRect(60, stripY, W - 120, 70);

  // Brand: DIGITAL REVOLUTION RADIO
  ctx.fillStyle = "#fafafa";
  ctx.font = "800 34px 'Archivo Black', 'Impact', system-ui, sans-serif";
  ctx.fillText("DIGITAL", 84, stripY + 18);
  ctx.fillStyle = "#dc2626";
  const w1 = ctx.measureText("DIGITAL ").width;
  ctx.fillText("REVOLUTION", 84 + w1, stripY + 18);
  ctx.fillStyle = "#fafafa";
  const w2 = ctx.measureText("REVOLUTION ").width;
  ctx.fillText(" RADIO", 84 + w1 + w2, stripY + 18);

  // Host on the right
  ctx.fillStyle = "#a1a1aa";
  ctx.font = "700 20px 'IBM Plex Mono', ui-monospace, monospace";
  const hostW = ctx.measureText(SITE_HOST).width;
  ctx.fillText(SITE_HOST, W - 60 - 24 - hostW, stripY + 25);
}

/**
 * ShareCard — button + modal for sharing the currently spinning track.
 * Generates a brutalist 1200x630 OG-image on the fly via HTML Canvas.
 * Provides: native share (mobile), X/Twitter, Facebook, Copy Link, Download PNG.
 *
 * Props:
 *   title  – track name (from useNowPlaying)
 *   artist – track artist (from useNowPlaying)
 *   compact – smaller button variant for use inside the persistent player bar
 */
export default function ShareCard({ title, artist, compact = false }) {
  const [open, setOpen] = useState(false);
  const [dataUrl, setDataUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [rendering, setRendering] = useState(false);
  const canvasRef = useRef(null);

  const canShare = !!(title && title.trim());

  const render = useCallback(() => {
    setRendering(true);
    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_W;
    canvas.height = CANVAS_H;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setRendering(false);
      return null;
    }
    paintCard(ctx, { title, artist });
    canvasRef.current = canvas;
    const url = canvas.toDataURL("image/png");
    setDataUrl(url);
    setRendering(false);
    return url;
  }, [title, artist]);

  useEffect(() => {
    if (open) render();
  }, [open, render]);

  const shareText = title
    ? `NOW SPINNING on Digital Revolution Radio: "${title}"${
        artist ? ` — ${artist}` : ""
      } 🤘`
    : "Tune into Digital Revolution Radio 🤘";
  const shareUrl = SITE_URL;

  // Facebook / LinkedIn / Threads all ignore the ``quote=`` param on their
  // sharer URLs and only use the shared page's OG tags. So we route those
  // shares through /api/share/spinning?title=&artist=& — that endpoint
  // renders bespoke OG tags with the track name baked in, giving FB a
  // proper "NOW SPINNING: Slayer — Cover" preview card instead of the
  // generic site card. Twitter/X still gets the direct site URL because it
  // honors ``text=`` in the tweet body.
  const socialShareUrl = title
    ? `${SITE_URL}/api/share/spinning?title=${encodeURIComponent(
        title,
      )}${artist ? `&artist=${encodeURIComponent(artist)}` : ""}`
    : SITE_URL;

  const twitterHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    shareText,
  )}&url=${encodeURIComponent(socialShareUrl)}&hashtags=DigitalRevolutionRadio,HeavyMetal,HardRock`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    socialShareUrl,
  )}&quote=${encodeURIComponent(shareText)}`;
  const redditHref = `https://www.reddit.com/submit?title=${encodeURIComponent(
    shareText,
  )}&url=${encodeURIComponent(socialShareUrl)}`;

  const handleDownload = () => {
    const url = dataUrl || render();
    if (!url) return;
    const a = document.createElement("a");
    const filename = title
      ? `drr-now-spinning-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`
      : "drr-now-spinning.png";
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Share card saved 🤘");
  };

  const handleNativeShare = async () => {
    // navigator.share with files works on iOS Safari / Android Chrome / Edge.
    const canonicalCanvas = canvasRef.current || (render() && canvasRef.current);
    if (!canonicalCanvas || !navigator.share) {
      toast.error("Native share unavailable — pick a network below");
      return;
    }
    try {
      canonicalCanvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], "drr-now-spinning.png", {
          type: "image/png",
        });
        try {
          if (
            navigator.canShare &&
            navigator.canShare({ files: [file] })
          ) {
            await navigator.share({
              files: [file],
              title: "Digital Revolution Radio — Now Spinning",
              text: shareText,
              url: shareUrl,
            });
          } else {
            await navigator.share({
              title: "Digital Revolution Radio — Now Spinning",
              text: shareText,
              url: shareUrl,
            });
          }
        } catch (e) {
          if (e?.name !== "AbortError") {
            toast.error("Share cancelled");
          }
        }
      }, "image/png");
    } catch (e) {
      toast.error("Could not prep share");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  /**
   * One-tap X shortcut. Skips the modal entirely — used by the //_QUICK_X
   * button next to the main Share button. Two things happen at once:
   *   1. The branded 1200×630 PNG downloads to the user's device so they
   *      can drag it into the tweet compose window (Twitter's URL intent
   *      doesn't accept image attachments).
   *   2. X's tweet compose window opens in a NEW TAB via a programmatic
   *      anchor click — never navigates the current tab, no popup blocker
   *      false-positives, works even when the browser strips noopener.
   */
  const handleQuickX = () => {
    if (!canShare) return;
    // Trigger the card download first while we still have the user
    // gesture on the stack. Silent on failure — the tweet still goes
    // out with the URL preview.
    try {
      handleDownload();
    } catch (e) {
      /* silent */
    }
    // Programmatic anchor click is the only bulletproof way to open a
    // new tab without redirecting the current one. window.open()
    // combined with the noopener flag returns null, which broke the
    // previous implementation's "did it open?" check and made the
    // fallback navigate the current tab away from DRR.
    const a = document.createElement("a");
    a.href = twitterHref;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    // Slight delay before removing so slower browsers finish the
    // navigation on the new tab first.
    setTimeout(() => a.remove(), 0);
  };

  return (
    <>
      <div className="inline-flex items-stretch gap-1.5 shrink-0" data-testid="share-btn-group">
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={!canShare}
          title={canShare ? "Share this spin" : "Waiting for a track…"}
          className={
            compact
              ? `shrink-0 w-9 h-9 flex items-center justify-center border transition-colors ${
                  canShare
                    ? "text-zinc-300 border-zinc-700 hover:text-red-500 hover:border-red-600"
                    : "text-zinc-700 border-zinc-800 opacity-60 cursor-not-allowed"
                }`
              : `inline-flex items-center gap-2 border transition-colors px-4 h-9 text-xs tracking-widest uppercase font-display ${
                  canShare
                    ? "text-zinc-100 border-zinc-700 hover:border-red-600 hover:text-red-500"
                    : "text-zinc-600 border-zinc-800 opacity-60 cursor-not-allowed"
                }`
          }
          aria-label="Share now spinning"
          data-testid="share-now-spinning-btn"
        >
          <Share2 className="w-4 h-4" strokeWidth={2.2} />
          {!compact && <span>Share</span>}
        </button>
        {/* One-tap X shortcut — user's preferred network. Auto-downloads the
            branded card PNG then opens X compose in a new tab with the tweet
            pre-filled, so they can drag-drop the image into the tweet before
            posting. Skips the modal entirely. */}
        <button
          type="button"
          onClick={handleQuickX}
          disabled={!canShare}
          title={canShare ? "Tweet this spin — card downloads for you to attach" : "Waiting for a track…"}
          className={
            compact
              ? `shrink-0 w-9 h-9 flex items-center justify-center border transition-colors ${
                  canShare
                    ? "text-zinc-300 border-zinc-700 hover:text-white hover:bg-black hover:border-white"
                    : "text-zinc-700 border-zinc-800 opacity-60 cursor-not-allowed"
                }`
              : `inline-flex items-center gap-2 border transition-colors px-3 h-9 text-xs tracking-widest uppercase font-display ${
                  canShare
                    ? "text-zinc-100 border-zinc-700 hover:bg-black hover:border-white"
                    : "text-zinc-600 border-zinc-800 opacity-60 cursor-not-allowed"
                }`
          }
          aria-label="Post to X"
          data-testid="share-quick-x-btn"
        >
          <svg
            className="w-3.5 h-3.5"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden
          >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          {!compact && <span>Post to X</span>}
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Share now spinning"
          onClick={() => setOpen(false)}
          data-testid="share-card-modal"
        >
          <div
            className="w-full max-w-2xl bg-zinc-950 border-2 border-zinc-700 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
              <div className="text-xs tracking-[0.35em] text-zinc-300 uppercase flex items-center gap-2">
                <span className="on-air-dot" aria-hidden />
                //_SHARE_THIS_SPIN
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close share modal"
                className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-zinc-900 transition-colors"
                data-testid="share-card-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Preview */}
            <div className="p-4 md:p-6 bg-black">
              <div
                className="border border-zinc-800 bg-zinc-950 aspect-[1200/630] w-full flex items-center justify-center overflow-hidden"
                data-testid="share-card-preview-wrapper"
              >
                {rendering && !dataUrl ? (
                  <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                ) : dataUrl ? (
                  <img
                    src={dataUrl}
                    alt="Now spinning share preview"
                    className="w-full h-full object-cover"
                    data-testid="share-card-preview-img"
                  />
                ) : (
                  <div className="text-xs text-zinc-500 tracking-widest uppercase">
                    //_generating…
                  </div>
                )}
              </div>
              <div className="mt-3 text-[10px] text-zinc-500 tracking-widest uppercase">
                //_1200×630 · optimised_for_x_facebook_reddit
              </div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border-t border-zinc-800">
              {typeof navigator !== "undefined" && navigator.share && (
                <ActionBtn
                  onClick={handleNativeShare}
                  testId="share-native-btn"
                  icon={<Share2 className="w-4 h-4" />}
                  label="Native Share"
                />
              )}
              <ActionBtn
                as="a"
                href={twitterHref}
                testId="share-twitter-btn"
                icon={
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                }
                label="Post on X"
              />
              <ActionBtn
                as="a"
                href={fbHref}
                testId="share-facebook-btn"
                icon={
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d="M22 12a10 10 0 1 0-11.56 9.87v-6.98H7.9V12h2.54V9.8c0-2.51 1.5-3.9 3.79-3.9 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.98A10 10 0 0 0 22 12z" />
                  </svg>
                }
                label="Post on FB"
              />
              <ActionBtn
                as="a"
                href={redditHref}
                testId="share-reddit-btn"
                icon={
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d="M22 12.1a2.36 2.36 0 0 0-3.98-1.68 11.4 11.4 0 0 0-5.98-1.85l1.02-4.8 3.35.72a1.72 1.72 0 1 0 .17-1.02l-3.72-.79a.5.5 0 0 0-.6.38l-1.14 5.35a11.42 11.42 0 0 0-6.09 1.84A2.36 2.36 0 0 0 2 14.55a2.34 2.34 0 0 0 1.4 2.14 4.1 4.1 0 0 0-.06.7c0 3.44 3.97 6.24 8.86 6.24s8.86-2.8 8.86-6.24c0-.24-.02-.47-.06-.7A2.34 2.34 0 0 0 22 12.1zM8.15 14.75a1.34 1.34 0 1 1 2.68 0 1.34 1.34 0 0 1-2.68 0zm6.68 3.72c-.9.7-2.16 1.05-3.63 1.05s-2.73-.35-3.63-1.05a.44.44 0 1 1 .54-.7c.72.55 1.83.86 3.09.86s2.37-.31 3.09-.86a.44.44 0 1 1 .54.7zm.68-2.38a1.34 1.34 0 1 1 0-2.68 1.34 1.34 0 0 1 0 2.68z" />
                  </svg>
                }
                label="Post to Reddit"
              />
              <ActionBtn
                onClick={handleCopyLink}
                testId="share-copy-btn"
                icon={
                  copied ? (
                    <Check className="w-4 h-4 text-red-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )
                }
                label={copied ? "Copied!" : "Copy Link"}
              />
              <ActionBtn
                onClick={handleDownload}
                testId="share-download-btn"
                icon={<Download className="w-4 h-4" />}
                label="Download PNG"
                accent
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ActionBtn({
  as = "button",
  href,
  onClick,
  icon,
  label,
  testId,
  accent = false,
}) {
  const cls = `flex items-center justify-center gap-2 py-3.5 px-3 border-r border-b border-zinc-800 text-xs tracking-widest uppercase font-display transition-colors ${
    accent
      ? "bg-red-600 text-white hover:bg-white hover:text-black"
      : "bg-zinc-950 text-zinc-100 hover:bg-zinc-900 hover:text-red-500"
  }`;
  if (as === "a") {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cls}
        data-testid={testId}
      >
        {icon}
        {label}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cls}
      data-testid={testId}
    >
      {icon}
      {label}
    </button>
  );
}
